import { Link } from "react-router-dom";
import { Shield, Mail, MapPin, Phone } from "lucide-react";

export default function PrivacyPolicy() {
  const lastUpdate = "23 settembre 2026";
  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="privacy-policy-page">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-8 h-8 text-labor-olive" />
        <div className="text-xs uppercase tracking-widest text-labor-muted">Documento legale</div>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl mb-2">Privacy Policy</h1>
      <p className="text-labor-muted text-sm mb-8">
        Informativa sul trattamento dei dati personali · Labor AI (app del CAF Patronato Labor di Bitonto) · Ultimo aggiornamento: {lastUpdate}
      </p>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">1. Titolare del trattamento</h2>
        <p className="text-sm leading-relaxed mb-3">
          Il Titolare del trattamento dei dati personali raccolti tramite l'app <strong>Labor AI</strong> e tramite il sito
          <em> caflaborbitontoapp.online</em> è il <strong>CAF Patronato Labor di Bitonto</strong> nella persona del suo
          rappresentante <strong>Vito Brilli</strong>.
        </p>
        <div className="text-sm space-y-1 text-labor-muted">
          <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> Via Zuavo n. 12 · 70032 Bitonto (BA), Italia</div>
          <div className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0" /> <a href="mailto:caflaborvitobitonto@gmail.com" className="text-labor-olive hover:underline">caflaborvitobitonto@gmail.com</a></div>
          <div className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0" /> 379 146 6566</div>
        </div>
        <p className="text-xs text-labor-muted mt-3 italic">
          Per esercitare i diritti previsti dal GDPR o per qualunque richiesta relativa alla privacy, l'interessato può
          scrivere all'indirizzo email sopra indicato.
        </p>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">2. Dati personali raccolti e finalità</h2>
        <p className="text-sm leading-relaxed mb-4">
          Labor AI raccoglie e tratta esclusivamente i dati personali strettamente necessari a fornire i servizi
          richiesti dall'utente. In particolare:
        </p>

        <div className="space-y-4 text-sm">
          <div>
            <div className="font-semibold text-labor-ink">a) Registrazione e gestione dell'account</div>
            <p className="mt-1 text-labor-muted">
              Al momento della registrazione raccogliamo: <strong>nome</strong>, <strong>cognome</strong>,
              <strong> indirizzo email</strong>, <strong>numero di telefono</strong> (obbligatorio, per verificare
              l'identità dell'assistito e per finalità di contatto operativo) e una <strong>password</strong> scelta
              dall'utente. La password non è mai memorizzata in chiaro: viene conservata come <em>hash bcrypt</em>.
              Facoltativamente l'utente può impostare un <strong>PIN numerico (4-6 cifre)</strong> per l'accesso rapido
              da dispositivo mobile.
            </p>
            <p className="mt-1 text-xs text-labor-muted italic">
              Base giuridica: esecuzione di un contratto/servizio richiesto dall'interessato (art. 6.1.b GDPR).
            </p>
          </div>

          <div>
            <div className="font-semibold text-labor-ink">b) Accesso tramite Google (opzionale)</div>
            <p className="mt-1 text-labor-muted">
              Se l'utente sceglie di accedere con il proprio account Google, riceviamo dal provider soltanto il
              <strong> nome</strong>, il <strong>cognome</strong> e l'<strong>indirizzo email</strong> associati
              all'account Google. Non abbiamo accesso alla password Google né ad altri dati del suo profilo.
              Il numero di telefono resta comunque obbligatorio e va inserito manualmente nel profilo per poter
              prenotare un appuntamento.
            </p>
          </div>

          <div>
            <div className="font-semibold text-labor-ink">c) Prenotazione appuntamenti</div>
            <p className="mt-1 text-labor-muted">
              Per ogni appuntamento memorizziamo: servizio richiesto (es. 730, ISEE, pensione, NASpI…), data e ora
              scelte, eventuali note libere fornite dall'utente, e i dati di contatto già presenti sul profilo
              (nome, email, telefono). Questi dati sono conservati per gestire l'appuntamento, contattare
              l'assistito in caso di variazioni e mantenere lo storico interno della pratica.
            </p>
          </div>

          <div>
            <div className="font-semibold text-labor-ink">d) Email transazionali (conferme e promemoria)</div>
            <p className="mt-1 text-labor-muted">
              L'app invia email transazionali di conferma prenotazione e promemoria appuntamento (il giorno
              precedente). Non inviamo email di marketing o newsletter. L'invio avviene tramite il servizio
              <strong> Resend</strong> (Resend, Inc., USA) oppure, come fallback, tramite <strong>SendGrid</strong>
              (Twilio SendGrid, USA). Quando questi servizi non sono configurati, le email sono soltanto registrate
              internamente e non spedite (modalità di test).
            </p>
          </div>

          <div>
            <div className="font-semibold text-labor-ink">e) Labor AI (assistente virtuale)</div>
            <p className="mt-1 text-labor-muted">
              Il chatbot Labor AI consente di ottenere risposte informative sui servizi del CAF. Ogni messaggio
              inviato dall'utente viene trasmesso al modello di intelligenza artificiale
              <strong> Anthropic Claude</strong>, accessibile tramite il gateway
              <strong> Emergent Integrations</strong>, per generare la risposta. Del dialogo memorizziamo il
              contenuto delle domande e delle risposte, un identificativo di sessione, la data/ora e il numero
              di Labor Point consumati. Se l'utente sceglie di dettare la domanda con il microfono, la trascrizione
              avviene <strong>direttamente nel browser</strong> tramite l'API Web Speech nativa del dispositivo:
              nessun file audio viene trasmesso o memorizzato dai nostri server.
            </p>
          </div>

          <div>
            <div className="font-semibold text-labor-ink">f) Labor Point e pagamenti</div>
            <p className="mt-1 text-labor-muted">
              Il sistema dei Labor Point (crediti prepagati per usare Labor AI oltre le 20 domande gratuite iniziali)
              prevede la registrazione dello storico degli acquisti e dei consumi di ciascun utente (importo,
              pacchetto acquistato, punti aggiunti/consumati, data). I pagamenti sono gestiti in modo esclusivo da
              <strong> Stripe Payments Europe Ltd.</strong>: i dati della carta di credito/debito non transitano
              né vengono mai memorizzati sui nostri server. Da parte nostra conserviamo unicamente l'identificativo
              di transazione Stripe, l'importo e l'esito, ai fini contabili e di supporto.
            </p>
          </div>

          <div>
            <div className="font-semibold text-labor-ink">g) Documenti condivisi</div>
            <p className="mt-1 text-labor-muted">
              L'operatore del CAF può caricare documenti in formato PDF (es. ricevute, moduli, comunicazioni) e
              condividerli con lo specifico assistito destinatario. I file sono accessibili unicamente dall'utente
              destinatario e dagli operatori autorizzati del CAF, e vengono <strong>eliminati automaticamente
              dopo 3 giorni</strong> per limitare la conservazione a quanto strettamente necessario.
            </p>
          </div>

          <div>
            <div className="font-semibold text-labor-ink">h) Feedback e ticket urgenza</div>
            <p className="mt-1 text-labor-muted">
              L'utente può inviare volontariamente un feedback (valutazione e messaggio libero). Il ticket urgenza
              conserva solo un contatore giornaliero e non contiene dati personali.
            </p>
          </div>

          <div>
            <div className="font-semibold text-labor-ink">i) Dati tecnici di funzionamento</div>
            <p className="mt-1 text-labor-muted">
              Per il corretto funzionamento tecnico dell'app usiamo <strong>token di sessione JWT</strong>
              (memorizzati nello storage locale del dispositivo dell'utente, non sono cookie di profilazione) e
              log tecnici temporanei (data/ora, endpoint richiesto, codice di risposta) generati dal server per
              motivi di sicurezza e diagnostica. Non utilizziamo cookie di profilazione né strumenti di
              tracciamento pubblicitario o analytics di terze parti.
            </p>
          </div>
        </div>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">3. Dati che NON raccogliamo</h2>
        <p className="text-sm leading-relaxed">
          Per trasparenza indichiamo espressamente che Labor AI <strong>non raccoglie</strong>: posizione GPS,
          contatti della rubrica, dati biometrici, foto o accesso alla fotocamera, file dell'utente (a parte i PDF
          caricati esclusivamente dall'operatore verso l'assistito), cronologia di navigazione, dati di terze parti.
          Non viene memorizzato alcun file audio: se l'utente usa il microfono nel chatbot, la trascrizione avviene
          nel browser tramite l'API Web Speech nativa e viene inviata al server solo come testo.
        </p>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">4. Servizi di terze parti (responsabili esterni)</h2>
        <p className="text-sm leading-relaxed mb-3">
          Per erogare i servizi ci avvaliamo dei seguenti fornitori, nominati Responsabili del trattamento ex
          art. 28 GDPR:
        </p>
        <ul className="text-sm space-y-2 list-disc list-inside text-labor-muted">
          <li><strong>Stripe Payments Europe Ltd.</strong> — elaborazione pagamenti con carta. Server nell'UE e USA.
            (<a href="https://stripe.com/it/privacy" target="_blank" rel="noopener" className="text-labor-olive hover:underline">stripe.com/it/privacy</a>)</li>
          <li><strong>Anthropic PBC</strong> — modello di intelligenza artificiale Claude, usato per le risposte del chatbot.
            (<a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener" className="text-labor-olive hover:underline">anthropic.com/legal/privacy</a>)</li>
          <li><strong>Emergent Integrations</strong> — gateway tecnico che veicola le chiamate al modello AI e l'autenticazione Google
            per conto dell'app.</li>
          <li><strong>Google LLC</strong> — solo se l'utente sceglie il login con Google, il provider di identità restituisce nome ed email.
            (<a href="https://policies.google.com/privacy" target="_blank" rel="noopener" className="text-labor-olive hover:underline">policies.google.com/privacy</a>)</li>
          <li><strong>Resend, Inc.</strong> e/o <strong>Twilio SendGrid Inc.</strong> — invio di email transazionali (conferma/promemoria).
            (<a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener" className="text-labor-olive hover:underline">resend.com/legal/privacy-policy</a> ·
            <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener" className="text-labor-olive hover:underline"> twilio.com/legal/privacy</a>)</li>
          <li><strong>MongoDB Atlas</strong> — hosting del database su cui sono memorizzati i dati dell'app.</li>
        </ul>
        <p className="text-xs text-labor-muted italic mt-3">
          Alcuni di questi fornitori hanno sede negli USA. Il trasferimento avviene sulla base delle Clausole
          Contrattuali Standard (SCC) approvate dalla Commissione Europea e delle ulteriori misure applicabili
          (art. 46 GDPR).
        </p>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">5. Base giuridica e finalità</h2>
        <ul className="text-sm space-y-1 list-disc list-inside text-labor-muted">
          <li><strong>Esecuzione contrattuale</strong> (art. 6.1.b GDPR): creazione account, prenotazione, uso del chatbot, gestione dei Labor Point.</li>
          <li><strong>Obblighi di legge</strong> (art. 6.1.c GDPR): tenuta della contabilità dei pagamenti ricevuti tramite Stripe.</li>
          <li><strong>Legittimo interesse</strong> (art. 6.1.f GDPR): sicurezza dell'account (log tecnici), prevenzione di abusi (obbligo di telefono verificabile).</li>
          <li><strong>Consenso</strong> (art. 6.1.a GDPR): invio di feedback volontario, uso opzionale del login Google.</li>
        </ul>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">6. Conservazione dei dati</h2>
        <ul className="text-sm space-y-1 list-disc list-inside text-labor-muted">
          <li>Dati dell'account: fino alla richiesta di cancellazione da parte dell'utente o alla chiusura del servizio.</li>
          <li>Storico prenotazioni e appuntamenti: <strong>fino a 5 anni</strong> dalla data dell'appuntamento, per motivi di continuità del servizio CAF.</li>
          <li>Storico chat con Labor AI: 24 mesi, poi anonimizzato o eliminato.</li>
          <li>Documenti PDF condivisi: <strong>3 giorni</strong>, con cancellazione automatica.</li>
          <li>Storico transazioni Stripe: <strong>10 anni</strong>, per obblighi fiscali e contabili.</li>
          <li>Log tecnici del server: massimo 30 giorni.</li>
        </ul>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">7. Sicurezza dei dati</h2>
        <p className="text-sm leading-relaxed">
          Adottiamo misure tecniche e organizzative appropriate per proteggere i dati personali, tra cui:
          trasmissione delle comunicazioni tramite protocollo <strong>HTTPS/TLS</strong>, hashing delle password
          con l'algoritmo <strong>bcrypt</strong>, autenticazione delle richieste tramite token JWT firmato,
          accesso ai dati riservato al solo personale autorizzato del CAF con account nominali, backup periodici
          del database e cancellazione automatica dei documenti condivisi decorsi 3 giorni.
        </p>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">8. Diritti dell'interessato</h2>
        <p className="text-sm leading-relaxed mb-3">
          Ai sensi degli artt. 15-22 GDPR l'utente ha diritto di:
        </p>
        <ul className="text-sm space-y-1 list-disc list-inside text-labor-muted">
          <li>ottenere l'<strong>accesso</strong> ai propri dati personali e riceverne copia (art. 15);</li>
          <li>ottenere la <strong>rettifica</strong> dei dati inesatti o incompleti (art. 16);</li>
          <li>ottenere la <strong>cancellazione</strong> dei dati ("diritto all'oblio", art. 17);</li>
          <li>ottenere la <strong>limitazione</strong> del trattamento (art. 18);</li>
          <li>ottenere la <strong>portabilità</strong> dei dati in formato strutturato (art. 20);</li>
          <li><strong>opporsi</strong> al trattamento basato sul legittimo interesse (art. 21);</li>
          <li><strong>revocare in ogni momento il consenso</strong> senza pregiudicare la liceità del trattamento fondato sul consenso prima della revoca;</li>
          <li>proporre <strong>reclamo</strong> all'Autorità Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener" className="text-labor-olive hover:underline">garanteprivacy.it</a>).</li>
        </ul>
        <div className="mt-4 border-l-4 border-labor-olive pl-4 py-2 bg-labor-skyBg">
          <p className="text-sm leading-relaxed">
            <strong>Come esercitare i tuoi diritti:</strong> è sufficiente inviare una email a{" "}
            <a href="mailto:caflaborvitobitonto@gmail.com" className="text-labor-olive hover:underline break-all">
              caflaborvitobitonto@gmail.com
            </a>{" "}
            indicando il tipo di richiesta e allegando (se necessario) un documento di identità. Risponderemo
            entro 30 giorni.
          </p>
        </div>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">9. Eliminazione dell'account</h2>
        <p className="text-sm leading-relaxed">
          L'utente può richiedere in qualsiasi momento la cancellazione del proprio account inviando una email a
          <strong> caflaborvitobitonto@gmail.com</strong>. Alla ricezione della richiesta, entro 30 giorni,
          eliminiamo il profilo personale, le sessioni di chat e i dati anagrafici; possono restare i soli dati
          strettamente necessari a rispettare obblighi di legge (es. registrazioni contabili delle transazioni
          Stripe, conservate per il periodo previsto dalla normativa fiscale).
        </p>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">10. Minori</h2>
        <p className="text-sm leading-relaxed">
          Il servizio è rivolto a persone maggiorenni. Non raccogliamo intenzionalmente dati di minori di 16 anni.
          Se un genitore o tutore ritiene che un minore ci abbia comunicato propri dati personali, può contattarci
          per la loro rimozione.
        </p>
      </section>

      <section className="paper-card p-6 mb-6">
        <h2 className="font-display text-2xl mb-3">11. Modifiche alla presente informativa</h2>
        <p className="text-sm leading-relaxed">
          La presente Privacy Policy può essere aggiornata per riflettere modifiche nei servizi offerti o novità
          normative. La versione aggiornata sarà sempre disponibile in questa pagina, con l'indicazione della data
          di ultimo aggiornamento in alto. Ti invitiamo a consultarla periodicamente.
        </p>
      </section>

      <div className="text-center mt-8">
        <Link to="/" className="text-labor-olive hover:underline text-sm">← Torna alla home</Link>
      </div>
    </div>
  );
}
